import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  useNavigate,
  useParams,
} from "react-router-dom";

import {
  collection,
  getDocs,
} from "firebase/firestore";

import {
  db,
} from "../firebase";

import "./LeagueMatches.css";


/* =========================================================
   CONFIG
========================================================= */

const TIMEZONE = "Africa/Dar_es_Salaam";

const FIFTEEN_MINUTES = 15 * 60 * 1000;


/* =========================================================
   COUNTRY CODE MAP
========================================================= */

const countryCodeMap = {
  Tanzania: "TZ",
  Kenya: "KE",
  Uganda: "UG",
  Rwanda: "RW",
  Burundi: "BI",

  Italy: "IT",
  England: "GB",
  "United Kingdom": "GB",
  Spain: "ES",
  France: "FR",
  Germany: "DE",
  Portugal: "PT",
  Netherlands: "NL",
  Belgium: "BE",

  Brazil: "BR",
  Argentina: "AR",
  Mexico: "MX",
  USA: "US",
  "United States": "US",

  Turkey: "TR",
  Greece: "GR",
  Scotland: "GB",
  Wales: "GB",
  Ireland: "IE",

  Norway: "NO",
  Sweden: "SE",
  Denmark: "DK",
  Finland: "FI",
  Switzerland: "CH",
  Austria: "AT",
  Poland: "PL",
  Ukraine: "UA",
  Russia: "RU",

  Japan: "JP",
  China: "CN",
  "South Korea": "KR",
  Australia: "AU",

  "South Africa": "ZA",
  Egypt: "EG",
  Morocco: "MA",
  Algeria: "DZ",
  Nigeria: "NG",
  Ghana: "GH",
  Senegal: "SN",
  Cameroon: "CM",
};


/* =========================================================
   COUNTRY FLAG
========================================================= */

const getCountryFlag = (country) => {
  if (!country) {
    return "🌍";
  }

  const cleanCountry = String(country).trim();

  const code =
    countryCodeMap[cleanCountry] ||
    cleanCountry.toUpperCase();

  if (!/^[A-Z]{2}$/.test(code)) {
    return "🌍";
  }

  return code
    .split("")
    .map((letter) =>
      String.fromCodePoint(
        127397 + letter.charCodeAt(0)
      )
    )
    .join("");
};


/* =========================================================
   DATE PARSER
========================================================= */

const parseDate = (date, time = "") => {
  if (!date) {
    return 0;
  }

  try {
    /* Firestore Timestamp */
    if (
      typeof date === "object" &&
      typeof date.toDate === "function"
    ) {
      const value = date.toDate().getTime();

      return Number.isNaN(value)
        ? 0
        : value;
    }

    /* JavaScript Date */
    if (date instanceof Date) {
      const value = date.getTime();

      return Number.isNaN(value)
        ? 0
        : value;
    }

    /* Firestore timestamp object */
    if (
      typeof date === "object" &&
      typeof date.seconds === "number"
    ) {
      const value = Number(date.seconds) * 1000;

      return Number.isNaN(value)
        ? 0
        : value;
    }

    const dateString = String(date).trim();

    if (!dateString) {
      return 0;
    }

    /*
      Date format:
      2026-09-13
      Time format:
      19:30

      Tanzania timezone: UTC+3
    */
    if (
      /^\d{4}-\d{2}-\d{2}$/.test(dateString)
    ) {
      let cleanTime = String(
        time || "00:00"
      ).trim();

      if (
        /^\d{1,2}:\d{2}$/.test(cleanTime)
      ) {
        const [
          hour,
          minute,
        ] = cleanTime.split(":");

        cleanTime =
          `${String(Number(hour)).padStart(2, "0")}:${minute}`;
      } else {
        cleanTime = "00:00";
      }

      const iso =
        `${dateString}T${cleanTime}:00+03:00`;

      const value = new Date(iso).getTime();

      return Number.isNaN(value)
        ? 0
        : value;
    }

    const cleanDate = dateString.replace(
      " ",
      "T"
    );

    const value = new Date(cleanDate).getTime();

    return Number.isNaN(value)
      ? 0
      : value;

  } catch (error) {
    console.error(
      "LEAGUE MATCH DATE ERROR:",
      error
    );

    return 0;
  }
};


/* =========================================================
   FORMAT MATCH DATE
========================================================= */

const formatMatchDate = (date, time) => {
  const timestamp = parseDate(date, time);

  if (!timestamp) {
    return "-";
  }

  return new Date(timestamp).toLocaleString(
    "en-GB",
    {
      timeZone: TIMEZONE,
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    }
  );
};


/* =========================================================
   MAIN COMPONENT
========================================================= */

export default function LeagueMatches() {
  const navigate = useNavigate();

  const {
    leagueName,
  } = useParams();

  const decodedLeagueName = decodeURIComponent(
    leagueName || ""
  );

  const [
    matches,
    setMatches,
  ] = useState([]);

  const [
    leagueCountry,
    setLeagueCountry,
  ] = useState("");

  const [
    loading,
    setLoading,
  ] = useState(true);

  const [
    now,
    setNow,
  ] = useState(Date.now());


  /* =======================================================
     LIVE CLOCK
  ======================================================= */

  useEffect(() => {
    const timer = setInterval(() => {
      setNow(Date.now());
    }, 1000);

    return () => {
      clearInterval(timer);
    };
  }, []);


  /* =======================================================
     LOAD MATCHES
  ======================================================= */

  useEffect(() => {
    let mounted = true;

    const loadMatches = async () => {
      try {
        setLoading(true);

        const snapshot = await getDocs(
          collection(db, "matches")
        );

        if (!mounted) {
          return;
        }

        const allMatches = snapshot.docs.map(
          (matchDoc) => ({
            id: matchDoc.id,
            ...matchDoc.data(),
          })
        );

        const selectedLeague =
          decodedLeagueName
            .trim()
            .toLowerCase();

        const selectedLeagueMatch =
          allMatches.find((match) => {
            const matchLeague = String(
              match.league ||
              match.competition ||
              ""
            )
              .trim()
              .toLowerCase();

            return matchLeague === selectedLeague;
          });

        setLeagueCountry(
          selectedLeagueMatch?.country || ""
        );

        const currentTime = Date.now();

        const result = allMatches.filter(
          (match) => {
            const matchLeague = String(
              match.league ||
              match.competition ||
              ""
            )
              .trim()
              .toLowerCase();

            const matchTime = parseDate(
              match.date,
              match.time
            );

            if (!matchTime) {
              return false;
            }

            const timeLeft =
              matchTime - currentTime;

            return (
              matchLeague === selectedLeague &&
              timeLeft > FIFTEEN_MINUTES
            );
          }
        );

        result.sort((a, b) => {
          const timeA = parseDate(
            a.date,
            a.time
          );

          const timeB = parseDate(
            b.date,
            b.time
          );

          return timeA - timeB;
        });

        setMatches(result);

      } catch (error) {
        console.error(
          "LEAGUE MATCHES LOAD ERROR:",
          error
        );

        if (mounted) {
          setMatches([]);
          setLeagueCountry("");
        }

      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    loadMatches();

    const refreshTimer = setInterval(
      loadMatches,
      60 * 1000
    );

    return () => {
      mounted = false;

      clearInterval(refreshTimer);
    };
  }, [decodedLeagueName]);


  /* =======================================================
     VISIBLE MATCHES
  ======================================================= */

  const visibleMatches = useMemo(() => {
    return matches
      .filter((match) => {
        const matchTime = parseDate(
          match.date,
          match.time
        );

        return (
          matchTime >
          now + FIFTEEN_MINUTES
        );
      })
      .sort((a, b) => {
        const timeA = parseDate(
          a.date,
          a.time
        );

        const timeB = parseDate(
          b.date,
          b.time
        );

        return timeA - timeB;
      });
  }, [matches, now]);


  /* =======================================================
     OPEN BETSCORE
  ======================================================= */

  const handleOpenMatch = (match) => {
    if (!match.id) {
      return;
    }

    navigate(
      `/betscores/${match.id}`,
      {
        state: match,
      }
    );
  };


  /* =======================================================
     BACK
  ======================================================= */

  const handleBack = () => {
    navigate(-1);
  };


  /* =======================================================
     LOADING
  ======================================================= */

  if (loading) {
    return (
      <main className="league-matches-page">
        <div className="league-matches-loading">
          <div className="league-matches-dots">
            <span></span>
            <span></span>
            <span></span>
          </div>

          <p>
            Loading league matches...
          </p>
        </div>
      </main>
    );
  }


  /* =======================================================
     UI
  ======================================================= */

  return (
    <main className="league-matches-page">

      {/* HEADER */}
      <header className="league-matches-header">

        <button
          type="button"
          className="league-matches-back"
          onClick={handleBack}
          aria-label="Go back"
        >
          ‹
        </button>

        <div className="league-header-info">
          <div className="league-country-flag">
            {getCountryFlag(leagueCountry)}
          </div>

          <div>
            <h1>
              {decodedLeagueName}
            </h1>

            <p>
              League matches
            </p>
          </div>
        </div>

      </header>


      {/* CONTENT */}
      <section className="league-matches-content">

        {visibleMatches.length === 0 ? (
          <div className="league-matches-empty">

            <div className="league-matches-empty-icon">
              {getCountryFlag(leagueCountry)}
            </div>

            <h2>
              No matches available
            </h2>

            <p>
              Hakuna matches za league hii kwa sasa.
            </p>

          </div>
        ) : (
          <div className="league-matches-list">

            {visibleMatches.map((match) => {
              const teamA =
                match.A ||
                match.teamA ||
                match.homeTeam ||
                "Team A";

              const teamB =
                match.B ||
                match.teamB ||
                match.awayTeam ||
                "Team B";

              const league =
                match.league ||
                match.competition ||
                decodedLeagueName;

              const matchTime = parseDate(
                match.date,
                match.time
              );

              const timeLeft = Math.max(
                0,
                matchTime - now
              );

              const hours = Math.floor(
                timeLeft /
                (60 * 60 * 1000)
              );

              const minutes = Math.floor(
                (
                  timeLeft %
                  (60 * 60 * 1000)
                ) /
                (60 * 1000)
              );

              const seconds = Math.floor(
                (
                  timeLeft %
                  (60 * 1000)
                ) /
                1000
              );

              return (
                <article
                  key={match.id}
                  className="league-match-card"
                  role="button"
                  tabIndex={0}
                  onClick={() =>
                    handleOpenMatch(match)
                  }
                  onKeyDown={(event) => {
                    if (
                      event.key === "Enter" ||
                      event.key === " "
                    ) {
                      event.preventDefault();

                      handleOpenMatch(match);
                    }
                  }}
                >

                  {/* MATCH TOP */}
                  <div className="league-match-top">

                    <span className="league-match-league">
                      {league}
                    </span>

                    <span className="league-match-status">
                      Upcoming
                    </span>

                  </div>


                  {/* TEAMS + COUNTRY */}
                  <div className="league-match-main">

                    <div className="league-match-center">

                      <div className="league-match-teams">

                        <span className="league-match-team">
                          {teamA}
                        </span>

                        <strong className="league-match-vs">
                          VS
                        </strong>

                        <span className="league-match-team">
                          {teamB}
                        </span>

                      </div>

                      <div className="league-match-league-name">
                        {league}
                      </div>

                      <div className="league-match-date">
                        {formatMatchDate(
                          match.date,
                          match.time
                        )}
                      </div>

                    </div>


                    <div className="league-match-country">

                      <div className="league-match-country-flag">
                        {getCountryFlag(
                          leagueCountry ||
                          match.country
                        )}
                      </div>

                      <span>
                        {leagueCountry ||
                          match.country ||
                          "International"}
                      </span>

                    </div>

                  </div>


                  {/* COUNTDOWN */}
                  <div className="league-match-countdown">
                    Starts in {hours}h {minutes}m {seconds}s
                  </div>

                </article>
              );
            })}

          </div>
        )}

      </section>
    </main>
  );
}